// src/prototype/pages/EAUseCaseHubPage.tsx
// ============================================================
// EA USE-CASE HUB  (/opportunity/:id/:tab  where tab ∈ brief | rationale | roi-model)
//
// Full-Content Drilldown v4 — the single converged "Use-Case Hub". A persistent
// use-case header (identity · verdict banner · KPI row) with tabs:
//   Decision Brief · Scoring Rationale · ROI Model
// Prev/next pages the WHOLE hub and preserves the active tab (the tab lives in the URL
// segment). The journey bar above is locked existing UX.
//
// ROI-tab carve-out (phase-3 spec): the ROI tab REUSES the existing EAROIModelPage as-is
// (no v4 ROI redesign), embedded under the hub header with its own chrome suppressed via
// the `embedded` prop. See the phase-3 flag in the PR notes.
//
// Tier gating is unchanged from phase 2: brief/rationale gated by canViewOpportunityDetail
// (freemium top-3), ROI gated Pro-only by can.viewRoiModel, Proceed → /upgrade when the
// user lacks execute.
// ============================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { APP_HEADER_HEIGHT_PX } from '../../components/header/headerMetrics';
import { useCaseExportUrl } from '../utils/exportReturn';
import {
  ArrowLeft, ChevronLeft, ChevronRight, ChevronRight as ArrowRightIcon, Check, CheckCircle2,
  Share2, Download, FileText, BarChart3, TrendingUp, Shield, Clock, DollarSign,
} from 'lucide-react';

import { EAJourneyBarFrame } from '../components/EAJourneyBarFrame';
import { StatTile } from '../components/StatTile';
import { EffortTagPill } from '../components/EffortTagPill';
import { VerdictPill } from '../components/VerdictPill';
import { BriefTab } from '../components/hub/BriefTab';
import { ScoringRationaleTab } from '../components/hub/ScoringRationaleTab';
import { AsOfChip } from '../components/hub/AsOfChip';
import EAROIModelPage from './EAROIModelPage';

import { MOCK_OPPORTUNITIES } from '../data/mockOpportunities';
import { getDecisionBrief, TOTAL_PORTFOLIO_SIZE } from '../data/decisionBrief';
import { LIBRARY_SIZE } from '../data/useCaseLibrary';
import { VERDICTS } from '../theme/decisionTokens';
import { getScores } from '../data/adapters';
import { hubFigures } from '../roi/hubFigures';
import { useEA } from '../contexts/EAContext';
import useEntitlements from '../hooks/useEntitlements';
import { computeRankedOpportunityIds, getOpportunityRank } from '../utils/opportunityRanking';
import useExecutePortfolio from '../hooks/useExecutePortfolio';


type HubView = 'brief' | 'rationale' | 'roi';
const SEG_TO_VIEW: Record<string, HubView> = { brief: 'brief', rationale: 'rationale', 'roi-model': 'roi' };
const VIEW_TO_SEG: Record<HubView, string> = { brief: 'brief', rationale: 'rationale', roi: 'roi-model' };

interface TabDef { key: HubView; label: string; icon: React.ComponentType<{ className?: string }>; proOnly?: boolean; }
const TABS: TabDef[] = [
  { key: 'brief', label: 'Decision Brief', icon: FileText },
  { key: 'rationale', label: 'Scoring Rationale', icon: BarChart3 },
  { key: 'roi', label: 'ROI Model', icon: TrendingUp, proOnly: true },
];

/** First sentence of a decision rationale (minus any leading "Recommendation:" label), for the one-line verdict row. */
const firstSentence = (text: string) =>
  text.replace(/^\s*Recommendation\s*[:\-–—]\s*/i, '').split(/(?<=[.!?])\s/)[0];

/** Freemium ROI-tab conversion panel (ROI is Pro-only even for the top-3). */
function RoiUpgradePanel({ onBack, onUpgrade }: { onBack: () => void; onUpgrade: () => void }) {
  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb] px-6 py-14 text-center">
      <div className="w-12 h-12 rounded-full bg-[#FFF1ED] text-[#FF6E4C] flex items-center justify-center mx-auto mb-3">
        <TrendingUp className="w-6 h-6" />
      </div>
      <p className="text-sm font-bold text-[#18365E]">The full ROI Model is a DVA Pro capability</p>
      <p className="text-xs text-[#717182] mt-1 max-w-md mx-auto">
        Scenario ranges, per-year projections, benchmark bands, and every assumption traced to a source. Available on DVA Pro.
      </p>
      <div className="mt-5 flex items-center justify-center gap-2">
        <button onClick={onBack} className="text-xs font-semibold text-[#18365E] border border-[#e5e7eb] rounded-lg px-4 py-2 hover:border-[#18365E] transition-colors">Back to Decision Brief</button>
        <button onClick={onUpgrade} className="bg-[#FF6E4C] text-white text-xs font-semibold px-4 py-2 rounded-lg hover:bg-[#e85f3f] transition-colors">Upgrade to Pro</button>
      </div>
    </div>
  );
}

export default function EAUseCaseHubPage() {
  const { id, tab } = useParams<{ id: string; tab: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const seg = tab && SEG_TO_VIEW[tab] ? tab : 'brief';
  const view = SEG_TO_VIEW[seg];

  const { categoryWeights } = useEA();
  const { canViewOpportunityDetail, can } = useEntitlements();
  const { isInPortfolio, proceed } = useExecutePortfolio();

  const rank = getOpportunityRank(id || '', categoryWeights);

  // Whole-hub gate: freemium may only open the top-N ranked use cases (matches the
  // former brief/rationale pages). Direct-URL access beyond that routes to /upgrade.
  useEffect(() => {
    if (!canViewOpportunityDetail(rank)) navigate('/upgrade', { replace: true });
  }, [rank, canViewOpportunityDetail, navigate]);

  // Sticky chrome measurements. Elements are tracked in state (callback refs) because they
  // only mount after the not-found early return below; the effects re-run when they appear.
  const [utilityEl, setUtilityEl] = useState<HTMLDivElement | null>(null);
  const [stripEl, setStripEl] = useState<HTMLDivElement | null>(null);
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);
  const [utilityH, setUtilityH] = useState(0);
  const [stripH, setStripH] = useState(0);
  const [stripShown, setStripShown] = useState(false);
  const tabBarRef = useRef<HTMLDivElement>(null);
  const prevNav = useRef<{ id?: string; seg?: string }>({});
  // Latest chrome height for the tab-change scroll effect (kept out of its deps on purpose).
  const chromeRef = useRef(APP_HEADER_HEIGHT_PX);
  chromeRef.current = APP_HEADER_HEIGHT_PX + utilityH + (stripShown ? stripH : 0);

  useEffect(() => {
    if (!utilityEl) return;
    const ro = new ResizeObserver(() => setUtilityH(utilityEl.offsetHeight));
    ro.observe(utilityEl);
    setUtilityH(utilityEl.offsetHeight);
    return () => ro.disconnect();
  }, [utilityEl]);

  useEffect(() => {
    if (!stripEl) return;
    const ro = new ResizeObserver(() => setStripH(stripEl.offsetHeight));
    ro.observe(stripEl);
    setStripH(stripEl.offsetHeight);
    return () => ro.disconnect();
  }, [stripEl]);

  // Compact strip: shown once the KPI row has scrolled up under the header + utility bar.
  // The sentinel sits at the bottom of the KPI row; rootMargin shrinks the root by the fixed
  // chrome so "out of view" means "behind the chrome", and only above (not below) counts.
  useEffect(() => {
    if (!sentinelEl) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        const above = entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0);
        setStripShown(!entry.isIntersecting && above);
      },
      { rootMargin: `-${APP_HEADER_HEIGHT_PX + utilityH}px 0px 0px 0px` },
    );
    io.observe(sentinelEl);
    return () => io.disconnect();
  }, [sentinelEl, utilityH]);

  // A new use case (id change, incl. first mount) scrolls to the top. A tab-only change must
  // not jump: if the tab bar has scrolled above the sticky chrome (header + utility bar +
  // compact strip when showing), bring it just under it.
  useEffect(() => {
    const prev = prevNav.current;
    prevNav.current = { id, seg };
    if (prev.id !== id) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }
    const bar = tabBarRef.current;
    if (!bar) return;
    const chrome = chromeRef.current;
    const top = bar.getBoundingClientRect().top;
    if (top < chrome) window.scrollTo({ top: window.scrollY + top - chrome, behavior: 'smooth' });
  }, [id, seg]);

  // Keyboard paging (←/→, or J next / K previous); ignored while typing. Handler ref is refreshed each render
  // below, after the early return, so hook order stays unconditional.
  const pageKeyRef = useRef<(dir: 1 | -1) => void>(() => {});
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (e.key === 'ArrowRight' || e.key === 'j' || e.key === 'J') pageKeyRef.current(1);
      else if (e.key === 'ArrowLeft' || e.key === 'k' || e.key === 'K') pageKeyRef.current(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const opportunity = useMemo(() => MOCK_OPPORTUNITIES.find((o) => o.id === id), [id]);
  const brief = useMemo(() => (opportunity ? getDecisionBrief(opportunity, rank) : null), [opportunity, rank]);

  // Paging order; freemium can only page the ranks it owns.
  const rankedIds = useMemo(() => {
    const all = computeRankedOpportunityIds(categoryWeights);
    return can.exploreBeyondTopN ? all : all.slice(0, 3);
  }, [categoryWeights, can.exploreBeyondTopN]);

  if (!opportunity || !brief) {
    return (
      <div className="min-h-screen bg-[#F5F7FA]">
        <EAJourneyBarFrame compact />
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="bg-white rounded-xl border border-[#e5e7eb] px-6 py-14 text-center">
            <p className="text-sm font-bold text-[#18365E]">Use case not found</p>
            <p className="text-xs text-[#717182] mt-1">No opportunity matches <span className="font-mono">{id}</span>.</p>
            <button onClick={() => navigate('/opportunities')} className="mt-5 bg-[#FF6E4C] text-white text-xs font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5 hover:bg-[#e85f3f] transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" />All opportunities
            </button>
          </div>
        </div>
      </div>
    );
  }

  const sc = getScores(brief);
  const fig = hubFigures(brief.id);
  const inPortfolio = isInPortfolio(brief.id);

  const idx = rankedIds.indexOf(brief.id);
  const prevId = idx > 0 ? rankedIds[idx - 1] : null;
  const nextId = idx >= 0 && idx < rankedIds.length - 1 ? rankedIds[idx + 1] : null;

  // Prev/next page the whole hub and preserve the active tab (the URL segment).
  const page = (targetId: string | null) => {
    if (targetId) navigate(`/opportunity/${targetId}/${seg}`, { state: location.state });
  };
  pageKeyRef.current = (dir) => page(dir === 1 ? nextId : prevId);
  // replace: Back returns to the Explorer, not through every tab visited.
  const goTab = (v: HubView) => navigate(`/opportunity/${brief.id}/${VIEW_TO_SEG[v]}`, { replace: true, state: location.state });

  const openCommandCenter = () => navigate(`/execute/${brief.id}`);
  const handleProceed = () => {
    // Proceeding is the Pro upgrade moment (phase-2 fix): route rather than add a finalist
    // freemium cannot act on.
    if (!can.execute) { navigate('/upgrade'); return; }
    proceed(brief.id);
  };

  const fromStep3 = location.state?.from === 'activate';
  const backLabel = fromStep3 ? 'Execution portfolio' : 'All opportunities';
  // returnTo WINS over the fromStep3 boolean. Previously the boolean took precedence, so a
  // brief opened from a command center came back to the /activate landing and the presenter
  // had to re-enter the initiative. `from: 'activate'` still drives chrome/labelling; it just
  // no longer decides the destination when the opener told us where it came from.
  const backPath =
    (location.state?.returnTo as string) || (fromStep3 ? '/activate' : '/opportunities');

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Journey bar — locked existing UX. */}
      <EAJourneyBarFrame compact />

      {/* Sticky utility bar — back out, or page ranked candidates in place (keeps the tab). */}
      <div ref={setUtilityEl} style={{ top: APP_HEADER_HEIGHT_PX }} className="sticky z-10 bg-white/95 backdrop-blur border-b border-[#e5e7eb] px-6 py-2.5">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate(backPath, { state: fromStep3 ? undefined : { returnToTab: 'explore' } })}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#717182] hover:text-[#18365E] transition-colors shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" />{backLabel}
          </button>
          <span className="text-[#e5e7eb]" aria-hidden="true">|</span>
          <span className="text-xs font-bold text-[#18365E] truncate">{brief.rank}. {brief.title}</span>
          <div className="ml-auto flex items-center gap-1 shrink-0">
            <span className="text-[10px] text-[#717182] mr-1.5 hidden sm:block">Rank {brief.rank} of {TOTAL_PORTFOLIO_SIZE} · {LIBRARY_SIZE} evaluated</span>
            <button onClick={() => page(prevId)} disabled={!prevId} aria-label="Previous ranked opportunity (keeps this tab)" className="w-7 h-7 rounded-lg border border-[#e5e7eb] flex items-center justify-center text-[#18365E] disabled:opacity-30 hover:border-[#18365E] transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => page(nextId)} disabled={!nextId} aria-label="Next ranked opportunity (keeps this tab)" className="w-7 h-7 rounded-lg border border-[#e5e7eb] flex items-center justify-center text-[#18365E] disabled:opacity-30 hover:border-[#18365E] transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Compact strip — sticky under the utility bar once the KPI row scrolls away. The wrapper
          is a zero-height sticky anchor so showing/hiding the strip never shifts the page. */}
      <div className="sticky z-10 h-0" style={{ top: APP_HEADER_HEIGHT_PX + utilityH }}>
        <div
          ref={setStripEl}
          aria-hidden={!stripShown}
          className={`absolute inset-x-0 top-0 bg-white/95 backdrop-blur border-b border-[#e5e7eb] px-6 py-2 transition-opacity ${stripShown ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <div className="max-w-6xl mx-auto flex items-center gap-3 flex-nowrap">
            {/* Title takes the flexible slot and truncates first. */}
            <span className="flex-1 min-w-0 truncate text-xs font-bold text-[#18365E]" title={brief.title}>{brief.title}</span>
            <span className="shrink-0"><VerdictPill verdict={brief.verdict} /></span>
            {/* KPIs: labels from 1440px up, values only from 1280px, hidden below. */}
            <span className="hidden min-[1280px]:flex shrink-0 items-center gap-2 min-[1440px]:gap-4 whitespace-nowrap text-[11px] text-[#4b5563]">
              <span><span className="hidden min-[1440px]:inline text-[#717182]">Payback </span><b className="text-[#18365E]">{fig.payback}</b></span>
              <span className="min-[1440px]:hidden text-[#717182]" aria-hidden="true">·</span>
              <span><span className="hidden min-[1440px]:inline text-[#717182]">Net 3-Year Value </span><b className="text-[#18365E]">{fig.netValue3Yr}</b></span>
              <span className="min-[1440px]:hidden text-[#717182]" aria-hidden="true">·</span>
              <span><span className="hidden min-[1440px]:inline text-[#717182]">Annual Benefit </span><b className="text-[#18365E]">{fig.annualBenefitRange}</b></span>
            </span>
            <div className="shrink-0 flex items-center gap-1" role="tablist">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  role="tab"
                  tabIndex={stripShown ? 0 : -1}
                  aria-selected={view === t.key}
                  onClick={() => goTab(t.key)}
                  className={`px-2.5 py-1.5 text-[11px] font-semibold whitespace-nowrap rounded-md transition-colors ${view === t.key ? 'bg-[#FFF1ED] text-[#FF6E4C]' : 'text-[#717182] hover:text-[#18365E]'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {inPortfolio ? (
              <button tabIndex={stripShown ? 0 : -1} onClick={openCommandCenter} className="shrink-0 whitespace-nowrap bg-[#18365E] text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-[#122a4a] transition-colors">
                Open Command Center<ArrowRightIcon className="w-3 h-3" />
              </button>
            ) : (
              <button tabIndex={stripShown ? 0 : -1} onClick={handleProceed} className="shrink-0 whitespace-nowrap bg-[#FF6E4C] text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-[#e85f3f] transition-colors">
                <Check className="w-3 h-3" />{VERDICTS[brief.verdict].verb} with Initiative
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* ── PERSISTENT HEADER: identity, actions, verdict, KPI row ── */}
        <div className="bg-white rounded-t-xl border border-b-0 border-[#e5e7eb] p-6 pb-5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="text-[10px] font-semibold text-dvaNavy uppercase tracking-wide">{brief.category}</span>
                <EffortTagPill tag={brief.effortTag} />
                {inPortfolio && (
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />In Execute portfolio
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-[#18365E] leading-tight">{brief.title}</h1>
            </div>
            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              <button onClick={() => navigator.clipboard?.writeText(window.location.href)} className="flex items-center gap-1.5 text-xs font-semibold text-[#18365E] border border-[#e5e7eb] rounded-lg px-3 py-2 hover:border-[#18365E] transition-colors">
                <Share2 className="w-3.5 h-3.5" />Share
              </button>
              <button onClick={() => window.open(useCaseExportUrl(brief.id), '_blank')} className="flex items-center gap-1.5 text-xs font-semibold text-[#18365E] border border-[#e5e7eb] rounded-lg px-3 py-2 hover:border-[#18365E] transition-colors">
                <Download className="w-3.5 h-3.5" />Export
              </button>
              {inPortfolio ? (
                <button onClick={openCommandCenter} className="bg-[#18365E] text-white text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 hover:bg-[#122a4a] transition-colors">
                  Open Command Center<ArrowRightIcon className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button onClick={handleProceed} className="bg-[#FF6E4C] text-white text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 hover:bg-[#e85f3f] transition-colors">
                  <Check className="w-3.5 h-3.5" />{VERDICTS[brief.verdict].verb} with Initiative
                </button>
              )}
            </div>
          </div>

          {/* One-line verdict; the full decision text is read once, in the Decision Brief. */}
          <div className="mt-4 flex items-center gap-3 min-w-0" title={brief.decisionText}>
            <VerdictPill verdict={brief.verdict} />
            <p className="text-sm text-[#4b5563] truncate">{firstSentence(brief.decisionText)}</p>
          </div>

          <div className="mt-4 grid grid-cols-2 lg:grid-cols-6 gap-3">
            <StatTile variant="dominant" label="Payback" value={fig.payback} icon={<Clock className="w-5 h-5" />} />
            <StatTile label="Net 3-Year Value" value={fig.netValue3Yr} icon={<DollarSign className="w-4 h-4" />} />
            <StatTile wrap className="col-span-2" label="Annual Benefit" value={fig.annualBenefitRange} icon={<TrendingUp className="w-4 h-4" />} />
            <StatTile label="Time to Value" value={brief.ttv} icon={<Clock className="w-4 h-4" />} />
            <StatTile label="Composite Score" value={`${sc.total} / 100`} icon={<BarChart3 className="w-4 h-4" />} />
          </div>
          <div ref={setSentinelEl} aria-hidden="true" className="h-px" />
          <div className="mt-2.5 flex items-center justify-between gap-3">
            <span className="text-[10px] text-[#717182]">Financial figures: Base Case scenario</span>
            <AsOfChip />
          </div>
        </div>

        {/* ── TABS + BODY ── */}
        <div className="border border-t-0 border-[#e5e7eb] rounded-b-xl bg-[#F5F7FA]">
          <div ref={tabBarRef} className="flex items-center gap-1 border-b border-[#e5e7eb] bg-white rounded-t-none px-3">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = view === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => goTab(t.key)}
                  className={`flex items-center gap-1.5 px-3.5 py-3 text-xs font-semibold border-b-2 -mb-px transition-colors ${active ? 'border-[#FF6E4C] text-[#FF6E4C]' : 'border-transparent text-[#717182] hover:text-[#18365E]'}`}
                >
                  <Icon className="w-3.5 h-3.5" />{t.label}
                  {t.proOnly && !can.viewRoiModel && <Shield className="w-3 h-3 opacity-60" />}
                </button>
              );
            })}
            {inPortfolio && (
              <button onClick={openCommandCenter} className="ml-auto flex items-center gap-1.5 px-3.5 py-3 text-xs font-bold text-[#18365E] hover:text-[#FF6E4C] transition-colors">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Command Center<ArrowRightIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="p-5">
            {view === 'brief' && <BriefTab brief={brief} onOpenRationale={() => goTab('rationale')} onOpenRoi={() => goTab('roi')} />}
            {view === 'rationale' && <ScoringRationaleTab brief={brief} />}
            {view === 'roi' && (
              can.viewRoiModel
                ? <EAROIModelPage embedded />
                : <RoiUpgradePanel onBack={() => goTab('brief')} onUpgrade={() => navigate('/upgrade')} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
